import React from 'react';
import { z } from 'zod';
import { safeUrl } from '../url';
import { styled } from 'styled-components';
import type { BlockDefinition } from '../types';

// Template hero — faithful rewrite of legacy molecules/TemplateHeroModern +
// TemplateStatisticsSection (Strapi sections.template-new-hero). Gradient band
// with heading + description + two CTAs and an image overhanging bottom-right,
// followed by a cream stats strip (milestone values) with column dividers.
export const templateHeroSchema = z.object({
  heading: z.string().min(1),
  description: z.string().optional(),
  imageUrl: z.string().optional(),
  primaryCta: z.object({ label: z.string(), href: safeUrl }).optional(),
  secondaryCta: z.object({ label: z.string(), href: safeUrl }).optional(),
  stats: z
    .array(z.object({ value: z.string().min(1), label: z.string().optional() }))
    .default([]),
});

type TemplateHeroProps = z.infer<typeof templateHeroSchema>;

const Band = styled.section`
  position: relative;
  overflow: hidden;
  border-radius: 0 0 80px 0;
  background: linear-gradient(93deg, #e8efff 0%, #fff 100%);
  padding: 0 2.4rem 4.8rem;

  @media (min-width: 62em) {
    padding: 0 0 8.5rem;
  }
`;

const Inner = styled.div`
  position: relative;
  max-width: 112rem;
  margin: 0 auto;

  @media (min-width: 62em) {
    display: grid;
    grid-template-columns: 1.4fr 1fr;
    margin-top: 5rem;
  }
`;

const Heading = styled.h1`
  margin: 0;
  padding-top: clamp(4.8rem, 50%, 16rem);
  padding-bottom: 2.4rem;
  color: #1a1b1f;
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 600;
  font-size: 3.4rem;

  @media (min-width: 62em) {
    font-size: 7.6rem;
  }
`;

const SubHeading = styled.p`
  margin: 0;
  color: #1a1b1f;
  font-family: 'DM Sans', sans-serif;
  font-weight: 400;
  font-size: 1.8rem;
  line-height: 1.5;

  @media (min-width: 62em) {
    font-size: 2.2rem;
    max-width: 80%;
  }
`;

const Ctas = styled.div`
  display: flex;
  gap: 1.6rem;
  margin-top: 4.8rem;
`;

const Btn = styled.a<{ $primary?: boolean }>`
  display: inline-block;
  padding: 1.2rem 2.4rem;
  border-radius: 1.6rem;
  font-family: 'DM Sans', sans-serif;
  font-size: 1.6rem;
  font-weight: 500;
  text-decoration: none;
  ${({ $primary }) =>
    $primary
      ? 'background: #0a3aa8; color: #ffffff; border: 1px solid #0a3aa8;'
      : 'background: transparent; color: #0a3aa8; border: 1px solid transparent;'}
`;

const HeroImage = styled.img`
  display: block;
  width: 100%;
  max-width: 100%;
  margin-top: 2.4rem;
  margin-bottom: -4.8rem;
  object-fit: contain;

  @media (min-width: 62em) {
    position: absolute;
    right: 0;
    bottom: -8.5rem;
    width: 50rem;
    max-width: 50rem;
    height: 50rem;
    max-height: 50rem;
    margin: 0;
  }
`;

const Stats = styled.div`
  display: flex;
  flex-direction: column;
  background: #fcfaf5;
  border-radius: 2.4rem;
  margin: 6.4rem 2.4rem 3.2rem;
  padding: 2.4rem;

  @media (min-width: 62em) {
    flex-direction: row;
    margin: 3.2rem 4.8rem;
    padding: 4rem;
  }
`;

const Stat = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 2.4rem 0;
  border-bottom: 1px solid rgba(216, 216, 216, 0.6);

  &:last-child {
    border-bottom: none;
  }

  @media (min-width: 62em) {
    padding: 0 4.8rem;
    border-bottom: none;
    border-right: 1px solid #d8d8d8;

    &:last-child {
      border-right: none;
    }
  }
`;

const StatValue = styled.p`
  margin: 0;
  color: #1a1b1f;
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 500;
  font-size: 5.6rem;
  text-align: center;
`;

const StatLabel = styled.p`
  margin: 0;
  color: #4e4f52;
  font-family: 'DM Sans', sans-serif;
  font-weight: 400;
  font-size: 1.8rem;
  text-align: center;
  max-width: 24ch;
  margin-left: auto;
  margin-right: auto;
`;

function TemplateHero({ heading, description, imageUrl, primaryCta, secondaryCta, stats }: TemplateHeroProps) {
  return (
    <>
      <Band>
        <Inner>
          <div>
            <Heading>{heading}</Heading>
            {description ? <SubHeading>{description}</SubHeading> : null}
            {primaryCta || secondaryCta ? (
              <Ctas>
                {primaryCta ? (
                  <Btn href={primaryCta.href} $primary>
                    {primaryCta.label}
                  </Btn>
                ) : null}
                {secondaryCta ? <Btn href={secondaryCta.href}>{secondaryCta.label}</Btn> : null}
              </Ctas>
            ) : null}
          </div>
          {imageUrl ? <HeroImage src={imageUrl} alt="" /> : null}
        </Inner>
      </Band>
      {stats.length > 0 ? (
        <Stats>
          {stats.map((s, i) => (
            <Stat key={`${s.value}-${i}`}>
              <StatValue>{s.value}</StatValue>
              {s.label ? <StatLabel>{s.label}</StatLabel> : null}
            </Stat>
          ))}
        </Stats>
      ) : null}
    </>
  );
}

export const templateHeroBlock: BlockDefinition<TemplateHeroProps> = {
  type: 'templateHero',
  schema: templateHeroSchema,
  Renderer: TemplateHero,
};
